/* eslint-disable comma-dangle */
/* eslint-disable import/no-cycle */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTheme } from 'styled-components';
import get from 'lodash/get';
import moment from 'moment';
import 'crux/dist/index.css'
import { DatePicker as DateSelector, DateInput } from 'crux';
import { updateAnswersArray } from '../redux/actions';
import { getLabelProps, getValidators, getWidth, showPopper, getTooltipProps } from './utils';
import { DOB_AGE_MESSAGE } from '../redux/selector/memberDetails';

const initialState = {
	// adding to make sure api call is done only when date is edited
	isEdited: false,
	isPrefilled: false,
	errorKey: 0,
	error: null
}
const DatePicker = ({ question, theme, handleChange, handleBlur, isEditMode, prefillData }) => {
	const [state, setState] = useState(initialState)
	const {
		pageId,
		config: {
			theme: {
				pageOverrides,
				components,
				global: { colorScheme, errorMessageColor, errorFontSize, labelStyles, spanText, inputStyles },
			},
		},
	} = theme;

	const labelProps = getLabelProps(
		'input',
		components,
		pageId,
		pageOverrides,
	);
	const {
		question_id: questionId,
		question_text: questionText,
		response,
		hint_title: tooltipTitle,
		hint_text: tooltipBody,
		hint_html: tooltipHTML,
		tooltip,
		question_status: questionStatus,
		question_status_message: questionStatusMessage,
		helper_text: helperText,
	} = question;

	const tooltipProps = getTooltipProps(tooltipTitle, tooltipBody, tooltipHTML || tooltip);

	useEffect(() => {
		if (isEditMode || question.response) {
			handleChange(question.response, false, true);
		}
	}, []);

	useEffect(() => {
		if(prefillData) {
			setState({ ...state, value: prefillData, isPrefilled: true })
			handleChange(prefillData, false, true);
		}
	}, [prefillData]);

	// crux's DatePicker/DateInput compute their own error text/border from their
	// own internal state and don't resync it when our Redux-driven status changes
	// later. Rather than fight that timing, render our own message directly from
	// Redux for this specific business rule.
	const isDobAgeInvalid = questionId === 'MemberDateOfBirth'
		&& questionStatus === 'invalid'
		&& questionStatusMessage === DOB_AGE_MESSAGE;

	const validators = getValidators(question);

	// Determines props for Calendar Popper (visible/hidden)
	const hasCalendarPopper = components.datepicker.showPopper
	const showPopperProps = !showPopper(hasCalendarPopper, validators) ?
		{ showCalendarPopper: false, showCalendarIcon: false } :
		{};

	// Checks if guide is enabled or not
	const hasGuide = get(components, 'datepicker.guide', false);

	// overrides floatingLabel to false in case of guide feature enabled
	const floatingLabel = hasGuide ? false : labelProps.floatingLabel;

	let datepicker;

	const commonProps = {
		labelText: questionText,
		id: questionId,
		response: prefillData || response,
		color: colorScheme,
		floatingLabel,
		width: getWidth('datepicker', pageId, pageOverrides),
		guide: hasCalendarPopper ? hasGuide : null,
		yearPlaceholder: !hasCalendarPopper ? 'YYYY' : null,
		monthPlaceholder: !hasCalendarPopper ? 'MM' : null,
		dayPlaceholder: !hasCalendarPopper ? 'DD' : null,
		questionStatus,
		isOptional: questionStatus === 'absent' || questionStatus === 'valid',
		isDatePrefilled: state.isPrefilled,
		...labelProps,
		...tooltipProps,
		...validators,
		// crux's own handleBlur only forwards to our onBlur prop when its own
		// internal minValue/maxValue check passes; if it fails, our onBlur (and
		// therefore our whole Redux validation chain below) never runs at all.
		// Widen crux's own boundary here so our own business-rule check (the
		// accident-product DOB age validator) is always given the chance to run.
		...(questionId === 'MemberDateOfBirth' ? {
			minValue: { value: '01/01/1900', error_message: '' },
			maxValue: { value: '12/31/2099', error_message: '' },
		} : {}),
		customError: state.error ? { error: false, error_message: '' } : validators.customError,
		helperText,
	}

	const responseFormatLength = validators.responseFormat.length;

	if (hasCalendarPopper) {
		datepicker = (
			<DateSelector
				key={ state.errorKey }
				onChange={ (value, error, isSelectedDate) => { 
					// isSelectedDate - true only if date is selected from calendar
					// value in the argument is always a valid date 
					// even if it is incomplete (e.g. 12/11/2). To prevent accepting incomplete dates
					// it is checked that the raw value is equals or not.
					//
					// CAUTION: only works if the accepted date format is MM/DD/YYYY
					// updating state whenever the date is selected from calendar popup
					if (!isSelectedDate) {
						return;
					}
					if ((prefillData && prefillData !== '') || (!state.value || state.value !== value)) {
						setState((prevState) => ({
							...prevState,
							isEdited: false,
							value,
							isPrefilled: false,
							error: null // a valid calendar selection clears any held error
						}))
					}
					handleChange(value, error, true, false, true)
				} }
				onValueChangeRaw={ (value) => {
					setState((prevState) => ({
						...prevState,
						isEdited: true,
						isPrefilled: false,
						value, // the raw input value is stored in state
						error: null // user is editing again, drop the previous error
					}))
				} }
				onBlur={ (value, error) => { 
					const strValue = typeof value === 'string' ? value : '';
					const isValidDate = strValue.length === responseFormatLength
						&& /^\d{2}\/\d{2}\/\d{4}$/.test(strValue)
						&& moment(strValue, 'MM/DD/YYYY', true).isValid();
					if(!isValidDate) {
						const isRequired = Boolean(get(validators, 'required.value'))
							|| questionStatus !== 'absent';
						if(isRequired) {
							const clearError = {
								error: true,
								error_message: get(validators, 'required.error_message') || 'This is a required field.',
							}
							setState((prevState) => ({
								...prevState,
								value: '',
								isPrefilled: false,
								error: clearError,
								errorKey: prevState.errorKey + 1,
							})) // clear the input and remount so it shows empty
							handleChange('', clearError, true, false, true) // update question object
						}
						else {
							setState((prevState) => ({ ...prevState, value: '', isPrefilled: false, error: null }))
							handleChange('', error, true, false, true)
						}
					}
					else{
						if(state.error) {
							setState((prevState) => ({ ...prevState, error: null }))
						}
						handleBlur(value, state.isEdited, error, true)
					}
				} }
				{ ...showPopperProps }
				{ ...commonProps }
				errorMessageColor = { errorMessageColor }
				errorFontSize={ errorFontSize }
				themeConfiguration={ { ...theme } }
				labelStyles={ labelStyles }
				spanText={ spanText }
				inputStyles={ inputStyles }
				value={ state.value } // bind the input with state
			/>
		)
	} else {
		datepicker = (
			<DateInput
				key={ state.errorKey }
				{ ...commonProps }
				onChange={ (value, error) => { handleChange(value, error, true, false, true) } }
				errorMessageColor = { errorMessageColor }
			/>
		)
	}

	const ownErrorMessage = get(state, 'error.error_message', '') || (isDobAgeInvalid ? questionStatusMessage : '');
	const showOwnError = Boolean(ownErrorMessage);

	return (
		<>
			{ /* fs-mask on the wrapper masks the text input and, since crux's calendar
			popper renders inline here rather than portaling to document.body, the
			popup grid too - no separate slot-level masking needed. */ }
			<div className="fs-mask">{ datepicker }</div>
			{ showOwnError && (
				<span
					style={ {
						display: 'block',
						color: errorMessageColor || '#ff0000',
						fontSize: errorFontSize || '0.75em',
						padding: '0.5em',
					} }
				>
					{ ownErrorMessage }
				</span>
			) }
		</>
	);
};

DatePicker.defaultProps = {
	sectionIndex: -1,
};
DatePicker.propTypes = {
	/* eslint-disable react/forbid-prop-types */
	question: PropTypes.object.isRequired,
	/* eslint-disable react/no-unused-prop-types */
	sectionIndex: PropTypes.number,
	handleChange: PropTypes.func.isRequired,
};

const mapStateToProps = (state, props) => {
	const questionId = get(props, 'question.question_id', '');
	const memberDataIndex = get(state, 'questions.memberDataIndex');
	const memberData = get(state, 'questions.otherParams.supplmentalresponse.member_data', []);
	if(memberData.length > parseInt(memberDataIndex, 10)) {
		const member = memberData[memberDataIndex];
		if(member && questionId.includes('BeneDateOfBirth')) {
			const memberKey = 'birthdate';
			const memberValue = member[memberKey];
			if(memberValue) {
				return {
					prefillData: memberValue,
				}
			}
		}
	}
	return {};
}

const mapDispatchToProps = (dispatch, props) => {
	const questionId = get(props, 'question.question_id', '');
	// question_status='absent' means the question is optional and for date input
	// if the date is null we don't consider it as error if DOB is an optional question
	// and there is no error in the date entered. This case is seen in the beneficiary 
	// section of Nationwide where DOB is optional and date can be null
	// const questionStatus = get(props, 'question.question_status', '');
	// let error;
	return {
		handleChange: (
			response,
			hasError,
			preventReflexiveCall,
			ignoreChildQuestionsOn,
			isChangeEvent
		) => {
			dispatch(
				updateAnswersArray({
					questionId,
					response,
					sectionIndex: props.sectionIndex,
					preventReflexiveCall,
					isError: hasError && hasError.error,
					questionStatusMessage: (hasError && hasError.error_message) || '',
					ignoreChildQuestionsOn,
					listParentId: props.listParentId,
					listIndex: props.listIndex,
					isChangeEvent
				}),
			);
		},
		handleBlur: (response, isEdited, hasError, isChangeEvent) => {
			dispatch(
				updateAnswersArray({
					questionId,
					response,
					preventReflexiveCall: isEdited,
					sectionIndex: props.sectionIndex,
					isError: hasError && hasError.error,
					questionStatusMessage: (hasError && hasError.error_message) || '',
					validate: get(props, 'question.dob_valid_flag', false) && isEdited,
					isChangeEvent
				}),
			);
		},
	};
};

export default withTheme(connect(mapStateToProps, mapDispatchToProps)(DatePicker));
